import { DOCUMENT } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { AuthService } from "../auth.service";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import {
  EXTENSION_AUTH_REDIRECT_URI_KEY,
  EXTENSION_AUTH_SOURCE_KEY,
  isValidExtensionRedirectUri,
} from "../extension-auth/extension-auth.constants";

@Component({
  selector: "app-callback",
  templateUrl: "./callback.component.html",
  standalone: false,
})
export class CallbackComponent implements OnInit {
  error: unknown;
  longLoading = false;

  constructor(
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  async ngOnInit() {
    setTimeout(() => (this.longLoading = true), 10000);
    try {
      const mobileRaw = this.route.snapshot.queryParamMap.get("mobile");
      const wantsMobileApp =
        mobileRaw === "1" || mobileRaw === "true" || mobileRaw === "yes";
      if (wantsMobileApp && this.auth.isLoginWithLink()) {
        const search = this.document.defaultView?.location.search ?? "";
        const query = search.startsWith("?") ? search.slice(1) : search;
        const target = query ? `dbc1:///?${query}` : "dbc1:///";
        this.document.defaultView?.location.replace(target);
        return;
      }
      if (this.auth.isLoginWithLink()) {
        await this.auth.loginWithEmailLink();
      } else {
        const redirectResult = await this.auth.getRedirectResult();
        if (redirectResult.continue === false) {
          this.clearExtensionAuthBridge();
          const r = redirectResult;
          if (r.reason === "requires_onboarding" && r.domain) {
            await this.router.navigate(["/onboarding"], {
              queryParams: { domain: r.domain },
            });
          }
          return;
        }
      }

      if (await this.completeExtensionAuthBridge()) {
        return;
      }

      const isOnboarding = this.route.snapshot.queryParams.onboarding as
        string | undefined;
      const domain = this.route.snapshot.queryParams.domain as
        string | undefined;
      const data = this.route.snapshot.queryParams.data as string | undefined;

      if (isOnboarding && domain && data) {
        await this.router.navigate(["/onboarding"], {
          queryParams: {
            domain,
            data,
          },
        });
        return;
      }

      const returnUrl = localStorage.getItem("returnUrl");
      if (returnUrl) {
        localStorage.removeItem("returnUrl");
        await this.router.navigateByUrl(returnUrl);
      } else {
        await this.router.navigate([""]);
      }
    } catch (e) {
      console.error("Login error:", e);
      this.clearExtensionAuthBridge();
      await this.router.navigate(["/login/error"]);
    }
  }

  private clearExtensionAuthBridge(): void {
    window.sessionStorage.removeItem(EXTENSION_AUTH_REDIRECT_URI_KEY);
    window.sessionStorage.removeItem(EXTENSION_AUTH_SOURCE_KEY);
  }

  private async completeExtensionAuthBridge(): Promise<boolean> {
    const redirectUri = window.sessionStorage.getItem(
      EXTENSION_AUTH_REDIRECT_URI_KEY,
    );
    if (!redirectUri || !isValidExtensionRedirectUri(redirectUri)) {
      this.clearExtensionAuthBridge();
      return false;
    }
    const source =
      window.sessionStorage.getItem(EXTENSION_AUTH_SOURCE_KEY) || "";
    this.clearExtensionAuthBridge();
    const issued = await this.auth.issueExtensionCustomToken();
    if (!issued?.customToken) {
      await this.router.navigate(["/login/error"]);
      return true;
    }
    const target = new URL(redirectUri);
    target.hash = new URLSearchParams({
      custom_token: issued.customToken,
      email: issued.email,
      ...(source ? { source } : {}),
    }).toString();
    this.document.defaultView?.location.replace(target.toString());
    return true;
  }
}
